import { useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * useOnboardingAutosave — dual-layer autosave for the Dive Brain onboarding wizard.
 *
 * 1. localStorage (instant, offline-safe, per-user scoped) — restored immediately on mount
 * 2. Server draft (survives cache clear / device switch) — debounced 1.5s after last change
 *
 * The hook is intentionally silent: consumers call save() whenever state changes;
 * the hook batches to avoid hammering the API.
 *
 * Draft is cleared automatically after successful completion via DELETE /api/onboarding/draft
 * (or the server also clears it when /onboarding/complete succeeds).
 */
export function useOnboardingAutosave(userId, snapshotFn) {
  const timer = useRef(null);
  const lastPayload = useRef(null);

  const localKey = userId ? `dms_onboarding_draft_v1:${userId}` : null;

  const readLocal = useCallback(() => {
    if (!localKey) return null;
    try {
      const raw = localStorage.getItem(localKey);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      console.debug("[onboarding-draft] localStorage read failed:", e?.name);
      return null;
    }
  }, [localKey]);

  const writeLocal = useCallback((payload) => {
    if (!localKey) return;
    try {
      localStorage.setItem(localKey, JSON.stringify({ ...payload, _saved_at: new Date().toISOString() }));
    } catch (e) { console.debug("[onboarding-draft] localStorage write failed:", e?.name); }
  }, [localKey]);

  const clearLocal = useCallback(() => {
    if (!localKey) return;
    try { localStorage.removeItem(localKey); } catch (e) { console.debug("[onboarding-draft] localStorage clear failed:", e?.name); }
  }, [localKey]);

  // Immediate localStorage save + debounced server save
  const save = useCallback(() => {
    const payload = snapshotFn();
    if (!payload) return;
    lastPayload.current = payload;
    writeLocal(payload);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        await api.put("/onboarding/draft", payload);
      } catch (e) {
        // Silent — localStorage still has it, so the user can still recover.
        console.debug("[onboarding-draft] server save failed:", e?.response?.status);
      }
    }, 1500);
  }, [snapshotFn, writeLocal]);

  // Flush on tab-close so nothing is lost if user closes the tab before the debounce fires.
  useEffect(() => {
    const flush = () => {
      if (lastPayload.current) writeLocal(lastPayload.current);
    };
    window.addEventListener("beforeunload", flush);
    document.addEventListener("visibilitychange", flush);
    return () => {
      window.removeEventListener("beforeunload", flush);
      document.removeEventListener("visibilitychange", flush);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [writeLocal]);

  return { save, readLocal, clearLocal };
}
